import jsPDF from 'jspdf';
import 'jspdf-autotable';

/**
 * Generates and downloads a comprehensive PDF report for multiple cases.
 * @param {Array} cases - Array of case objects
 * @param {string} companyName - Name of the law firm/company
 * @param {string} companyLogo - Base64 string or image URL
 */
const DownloadFullCaseReportPdf = (cases, companyName, companyLogo) => {
  return new Promise((resolve, reject) => {
    try {
      if (!cases || !Array.isArray(cases) || cases.length === 0) {
        throw new Error("No cases data available to download.");
      }

      // 1. Initialize Document (Portrait, A4)
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      let currentY = 15;

      // Date Helper
      const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return dateStr.split('T')[0];
      };

      // Helper function to render header on pages
      const renderHeader = () => {
        currentY = 12;
        if (companyLogo) {
          try {
            doc.addImage(companyLogo, 'PNG', 14, currentY, 15, 15);
            doc.setFontSize(16);
            doc.setFont('helvetica', 'bold');
            doc.text(companyName || "Legal Management System", 32, currentY + 10);
          } catch (e) {
            doc.setFontSize(16);
            doc.setFont('helvetica', 'bold');
            doc.text(companyName || "Legal Management System", 14, currentY + 8);
          }
        } else {
          doc.setFontSize(16);
          doc.setFont('helvetica', 'bold');
          doc.text(companyName || "Legal Management System", 14, currentY + 8);
        }

        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        const todayDate = new Date().toLocaleDateString('en-GB');
        doc.text(`Generated On: ${todayDate}`, pageWidth - 14, currentY + 8, { align: 'right' });

        currentY += 18;
        doc.setFontSize(13);
        doc.setFont('helvetica', 'bold');
        doc.text(`ALL CASES SUMMARY REPORT (${cases.length} Records)`, pageWidth / 2, currentY, { align: 'center' });

        currentY += 4;
        doc.setDrawColor(200, 200, 200);
        doc.line(14, currentY, pageWidth - 14, currentY);
        currentY += 8;
      };

      // Render initial header
      renderHeader();

      // Loop over all cases
      cases.forEach((row, caseIdx) => {
        // Space check: If close to page bottom, add a new page
        if (currentY > pageHeight - 60) {
          doc.addPage();
          renderHeader();
        }

        // Case Header Banner
        doc.setFillColor(240, 244, 248);
        doc.rect(14, currentY, pageWidth - 28, 8, 'F');
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(25, 118, 210);
        doc.text(`CASE #${caseIdx + 1}: ${row.case_title || 'Untitled Case'} (Case No: ${row.case_no || 'N/A'})`, 17, currentY + 5.5);
        
        doc.setTextColor(0, 0, 0); // Reset text color
        currentY += 11;

        // 1. Case Details AutoTable
        const primaryDetailsRows = [
          [
            { content: "Case No:", styles: { fontStyle: 'bold' } }, row.case_no || 'N/A',
            { content: "FIR No:", styles: { fontStyle: 'bold' } }, row.fir_no || 'N/A'
          ],
          [
            { content: "Case Type:", styles: { fontStyle: 'bold' } }, row.case_type_name || 'N/A',
            { content: "Stage Name:", styles: { fontStyle: 'bold' } }, row.stage_name || 'N/A'
          ],
          [
            { content: "Court Name:", styles: { fontStyle: 'bold' } }, row.court_name || 'N/A',
            { content: "Judge Name:", styles: { fontStyle: 'bold' } }, row.judge_name || 'N/A'
          ],
          [
            { content: "Filing Date:", styles: { fontStyle: 'bold' } }, formatDate(row.filing_date),
            { content: "Opposite Party:", styles: { fontStyle: 'bold' } }, row.opposite_party || 'N/A'
          ],
          [
            { content: "Priority:", styles: { fontStyle: 'bold' } }, row.priority || 'N/A',
            { content: "Status:", styles: { fontStyle: 'bold' } }, row.status || 'N/A'
          ]
        ];

        doc.autoTable({
          startY: currentY,
          body: primaryDetailsRows,
          theme: 'plain',
          bodyStyles: { fontSize: 8.5, cellPadding: 2, valign: 'middle' },
          columnStyles: {
            0: { cellWidth: 28 },
            1: { cellWidth: 62 },
            2: { cellWidth: 28 },
            3: { cellWidth: 64 }
          }
        });

        currentY = doc.lastAutoTable.finalY + 5;

        // 2. Clients Table
        doc.setFontSize(9.5);
        doc.setFont('helvetica', 'bold');
        doc.text("Clients Information", 14, currentY);
        currentY += 3;

        const totalClients = Math.max(row.client_names?.length || 0, row.client_emails?.length || 0);
        const clientRows = [];

        if (totalClients > 0) {
          for (let i = 0; i < totalClients; i++) {
            clientRows.push([
              row.client_names?.[i] || 'N/A',
              row.client_emails?.[i] || 'N/A'
            ]);
          }
        } else {
          clientRows.push(['No client linked', 'N/A']);
        }

        doc.autoTable({
          startY: currentY,
          head: [["Client Name", "Email Address"]],
          body: clientRows,
          theme: 'grid',
          headStyles: { fillColor: [74, 85, 104], fontSize: 8.5, cellPadding: 2 },
          bodyStyles: { fontSize: 8, cellPadding: 2 }
        });

        currentY = doc.lastAutoTable.finalY + 5;

        // 3. Hearings Table
        doc.setFontSize(9.5);
        doc.setFont('helvetica', 'bold');
        doc.text(`Hearing History (${row.hearings ? row.hearings.length : 0})`, 14, currentY);
        currentY += 3;

        if (!row.hearings || row.hearings.length === 0) {
          doc.setFontSize(8.5);
          doc.setFont('helvetica', 'italic');
          doc.text("No hearing records tracking found for this case.", 14, currentY + 3);
          currentY += 8;
        } else {
          const hearingHeaders = ["S.No", "Date", "Next Date", "Room", "Judge", "Status", "Summary "];
          const hearingRows = row.hearings.map((h, idx) => [
            idx + 1,
            formatDate(h.hearing_date),
            formatDate(h.next_hearing_date),
            h.court_room || '-',
            h.judge_name || '-',
            h.hearing_status || '-',
            `Summary: ${h.summary || 'N/A'}\nOrder: ${h.order_details || 'N/A'}`
          ]);

          doc.autoTable({
            startY: currentY,
            head: [hearingHeaders],
            body: hearingRows,
            theme: 'striped',
            headStyles: { fillColor: [25, 118, 210], fontSize: 8.5, halign: 'left', cellPadding: 2 },
            bodyStyles: { fontSize: 8, valign: 'top', cellPadding: 2 },
            columnStyles: {
              0: { cellWidth: 10 },
              1: { cellWidth: 22 },
              2: { cellWidth: 22 },
              3: { cellWidth: 18 },
              4: { cellWidth: 22 },
              5: { cellWidth: 20 },
              6: { cellWidth: 68 }
            }
          });

          currentY = doc.lastAutoTable.finalY + 8;
        }

        // Divider Line between cases
        if (caseIdx < cases.length - 1) {
          doc.setDrawColor(220, 220, 220);
          doc.line(14, currentY, pageWidth - 14, currentY);
          currentY += 8;
        }
      });

      // 4. Footer - Page Numbers
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(150, 150, 150);
        doc.text(`Page ${i} of ${totalPages}`, pageWidth - 14, pageHeight - 8, { align: 'right' });
        doc.text("Confidential - Case Report", 14, pageHeight - 8);
      }

      // Save PDF File
      const fileName = `Full_Case_Report_${new Date().toISOString().slice(0, 10)}.pdf`;
      doc.save(fileName);

      resolve();
    } catch (error) {
      reject(error);
    }
  });
};

export default DownloadFullCaseReportPdf;